import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import {
  PurchaseOrderStatus,
  type PurchaseOrderDetail,
  type PurchaseOrderLineSummary,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/LoadingState';
import { PageHeader } from '@/components/ui/PageHeader';
import { useBranch } from '@/hooks/useBranch';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorMessage } from '@/services/apiClient';
import { PERMISSIONS } from '@optiflow/types';
import { usePurchaseOrderQuery } from '../api/purchase-orders.api';
import {
  usePostPurchaseReceiptMutation,
  type PurchaseReceiptLineInput,
} from '../api/purchase-receipts.api';

/** One lot's share of a line, as the screen holds it while it is being entered. */
interface Allocation {
  id: string;
  quantity: string;
  lotNumber: string;
  expiryDate: string;
  manufacturedDate: string;
}

interface LineEntry {
  /** A line without lots receives a single quantity; a lot-tracked one receives one row per lot. */
  quantity: string;
  allocations: Allocation[];
}

const newAllocation = (): Allocation => ({
  id: Math.random().toString(36).slice(2),
  quantity: '',
  lotNumber: '',
  expiryDate: '',
  manufacturedDate: '',
});

const asQuantity = (value: string): number => {
  const parsed = Number(value.replace(/[^\d]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Counting a delivery in against its order.
 *
 * A page rather than a dialog: entering two lots of one item with their expiry dates is real work,
 * and a small modal makes it error-prone on the tablet this is used on. Only lines the supplier
 * still owes something on can be counted, and nothing is filled in by default — what arrived is
 * something a person reads off the boxes, not something the screen should assume.
 *
 * The screen keeps the client honest about its limits: it adds the lots up per line and warns
 * before sending, but the server is what refuses an over-receipt, and its refusal is shown as it
 * comes with the entered quantities left exactly as they were.
 */
export function ReceiveGoodsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>();
  const { activeBranch } = useBranch();
  const { can } = usePermissions();

  const [entries, setEntries] = useState<Record<string, LineEntry>>({});
  const [deliveryReference, setDeliveryReference] = useState('');

  const { data, isPending, isError, error, refetch } = usePurchaseOrderQuery(orderId);
  const post = usePostPurchaseReceiptMutation();

  const order: PurchaseOrderDetail | undefined = data;

  const outstanding = useMemo(
    () => (order?.lines ?? []).filter((line) => line.quantityRemaining > 0),
    [order],
  );

  if (isPending) return <LoadingState />;
  if (isError || !order) {
    return (
      <div className="space-y-3">
        <Alert tone="error">{apiErrorMessage(error) ?? t('purchasing.orders.loadError')}</Alert>
        <Button variant="secondary" onClick={() => void refetch()}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  const isOpen =
    order.status === PurchaseOrderStatus.ORDERED ||
    order.status === PurchaseOrderStatus.PARTIALLY_RECEIVED;
  const atOrderBranch = activeBranch?.id === order.branchId;
  const canReceive = can(PERMISSIONS.PURCHASING_RECEIVE);

  if (!canReceive) return <Alert tone="error">{t('purchasing.receipts.notAllowed')}</Alert>;

  if (!isOpen) {
    return (
      <div className="space-y-3">
        <Alert tone="warning">{t('purchasing.receipts.notReceivable')}</Alert>
        <Button variant="secondary" onClick={() => void navigate(`/purchasing/orders/${order.id}`)}>
          {t('purchasing.orders.backToOrder')}
        </Button>
      </div>
    );
  }

  const entryFor = (lineId: string): LineEntry =>
    entries[lineId] ?? { quantity: '', allocations: [] };

  const setEntry = (lineId: string, patch: Partial<LineEntry>) =>
    setEntries((current) => ({ ...current, [lineId]: { ...entryFor(lineId), ...patch } }));

  /** What this delivery counts in for a line: one number, or the lots added up. */
  const countedFor = (line: PurchaseOrderLineSummary): number => {
    const entry = entryFor(line.id);
    if (!line.isBatchTracked) return asQuantity(entry.quantity);
    return entry.allocations.reduce(
      (total, allocation) => total + asQuantity(allocation.quantity),
      0,
    );
  };

  const payloadLines = (): PurchaseReceiptLineInput[] =>
    outstanding.flatMap((line) => {
      const entry = entryFor(line.id);

      if (line.isBatchTracked) {
        return entry.allocations
          .filter((allocation) => asQuantity(allocation.quantity) > 0)
          .map((allocation) => ({
            purchaseOrderLineId: line.id,
            quantityReceived: asQuantity(allocation.quantity),
            lotNumber: allocation.lotNumber.trim(),
            ...(allocation.expiryDate ? { expiryDate: allocation.expiryDate } : {}),
            ...(allocation.manufacturedDate
              ? { manufacturedDate: allocation.manufacturedDate }
              : {}),
          }));
      }

      const quantity = asQuantity(entry.quantity);
      return quantity > 0 ? [{ purchaseOrderLineId: line.id, quantityReceived: quantity }] : [];
    });

  const lines = payloadLines();
  const totalCounted = lines.reduce((total, line) => total + line.quantityReceived, 0);

  const overReceived = outstanding.filter((line) => countedFor(line) > line.quantityRemaining);
  const missingLot = lines.some((line) => line.lotNumber !== undefined && !line.lotNumber);
  const canPost =
    atOrderBranch &&
    lines.length > 0 &&
    overReceived.length === 0 &&
    !missingLot &&
    !post.isPending;

  const submit = () => {
    if (!canPost) return;
    post.mutate(
      {
        purchaseOrderId: order.id,
        purchaseOrderVersion: order.version,
        ...(deliveryReference.trim()
          ? { supplierDeliveryReference: deliveryReference.trim() }
          : {}),
        lines,
      },
      { onSuccess: (receipt) => void navigate(`/purchasing/receipts/${receipt.id}`) },
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('purchasing.receipts.receiveTitle')}
        subtitle={t('purchasing.receipts.receiveSubtitle', {
          reference: order.reference,
          supplier: order.supplierName,
        })}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => void navigate(`/purchasing/orders/${order.id}`)}
              disabled={post.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button onClick={submit} isLoading={post.isPending} disabled={!canPost}>
              {t('purchasing.receipts.post')}
            </Button>
          </div>
        }
      />

      {!atOrderBranch ? (
        <Alert tone="warning">
          {t('purchasing.receipts.wrongBranch', { branch: order.branchName })}
        </Alert>
      ) : null}
      {post.isError ? (
        <Alert tone="error">
          {apiErrorMessage(post.error) ?? t('purchasing.receipts.postError')}
        </Alert>
      ) : null}
      {post.isError ? <Alert tone="info">{t('purchasing.receipts.inputKept')}</Alert> : null}
      {overReceived.length > 0 ? (
        <Alert tone="warning">{t('purchasing.receipts.overReceiveWarning')}</Alert>
      ) : null}

      <Card>
        <CardHeader title={t('purchasing.receipts.deliverySection')} />
        <CardBody className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-0.5">
            <span className="text-muted text-xs">{t('purchasing.orders.fields.branch')}</span>
            <p className="text-ink text-sm">{order.branchName}</p>
          </div>
          <div className="space-y-0.5">
            <span className="text-muted text-xs">{t('purchasing.receipts.fields.reference')}</span>
            <p className="text-ink text-sm">{t('purchasing.receipts.referenceOnPost')}</p>
          </div>
          <Input
            label={t('purchasing.receipts.fields.deliveryReference')}
            value={deliveryReference}
            hint={t('purchasing.receipts.fields.deliveryReferenceHint')}
            onChange={(event) => setDeliveryReference(event.target.value)}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title={t('purchasing.receipts.linesSection')}
          subtitle={t('purchasing.receipts.linesHint')}
        />
        <CardBody className="space-y-4">
          {outstanding.length === 0 ? (
            <p className="text-muted text-sm">{t('purchasing.receipts.nothingOutstanding')}</p>
          ) : (
            outstanding.map((line) => {
              const entry = entryFor(line.id);
              const lotted = line.isBatchTracked;
              const allocations =
                lotted && entry.allocations.length === 0 ? [newAllocation()] : entry.allocations;
              const counted = countedFor(line);
              const tooMany = counted > line.quantityRemaining;

              return (
                <section
                  key={line.id}
                  className="border-line space-y-3 rounded-lg border p-3"
                  aria-label={line.sku}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-ink text-sm font-medium" dir="auto">
                        {line.variantName ?? line.productName}
                      </p>
                      <p className="text-muted font-mono text-xs" dir="ltr">
                        {line.sku}
                      </p>
                    </div>
                    <dl className="text-muted flex flex-wrap gap-4 text-xs">
                      <span>
                        {t('purchasing.orders.fields.ordered')}{' '}
                        <span dir="ltr">{line.quantityOrdered}</span>
                      </span>
                      <span>
                        {t('purchasing.orders.fields.received')}{' '}
                        <span dir="ltr">{line.quantityReceived}</span>
                      </span>
                      <span>
                        {t('purchasing.orders.fields.cancelled')}{' '}
                        <span dir="ltr">{line.quantityCancelled}</span>
                      </span>
                      <span className="text-ink font-medium">
                        {t('purchasing.orders.fields.remaining')}{' '}
                        <span dir="ltr">{line.quantityRemaining}</span>
                      </span>
                    </dl>
                  </div>

                  {!lotted ? (
                    <div className="flex flex-wrap items-end gap-3">
                      <Input
                        label={t('purchasing.receipts.fields.quantityToReceive')}
                        className="w-32"
                        dir="ltr"
                        inputMode="numeric"
                        value={entry.quantity}
                        error={tooMany ? t('purchasing.receipts.tooMany') : undefined}
                        aria-label={t('purchasing.receipts.quantityFor', { sku: line.sku })}
                        onChange={(event) =>
                          setEntry(line.id, { quantity: event.target.value.replace(/[^\d]/g, '') })
                        }
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setEntry(line.id, { quantity: String(line.quantityRemaining) })
                        }
                      >
                        {t('purchasing.receipts.receiveAll')}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {allocations.map((allocation, index) => (
                        <div
                          key={allocation.id}
                          className="bg-canvas grid gap-3 rounded-lg p-3 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-end"
                        >
                          <Input
                            label={t('purchasing.receipts.fields.lotNumber')}
                            dir="ltr"
                            value={allocation.lotNumber}
                            aria-label={t('purchasing.receipts.lotFor', {
                              sku: line.sku,
                              index: index + 1,
                            })}
                            onChange={(event) =>
                              setEntry(line.id, {
                                allocations: allocations.map((candidate) =>
                                  candidate.id === allocation.id
                                    ? { ...candidate, lotNumber: event.target.value }
                                    : candidate,
                                ),
                              })
                            }
                          />
                          <Input
                            label={t('purchasing.receipts.fields.quantityToReceive')}
                            dir="ltr"
                            inputMode="numeric"
                            value={allocation.quantity}
                            aria-label={t('purchasing.receipts.lotQuantityFor', {
                              sku: line.sku,
                              index: index + 1,
                            })}
                            onChange={(event) =>
                              setEntry(line.id, {
                                allocations: allocations.map((candidate) =>
                                  candidate.id === allocation.id
                                    ? {
                                        ...candidate,
                                        quantity: event.target.value.replace(/[^\d]/g, ''),
                                      }
                                    : candidate,
                                ),
                              })
                            }
                          />
                          <Input
                            type="date"
                            label={t('purchasing.receipts.fields.expiryDate')}
                            value={allocation.expiryDate}
                            aria-label={t('purchasing.receipts.expiryFor', {
                              sku: line.sku,
                              index: index + 1,
                            })}
                            onChange={(event) =>
                              setEntry(line.id, {
                                allocations: allocations.map((candidate) =>
                                  candidate.id === allocation.id
                                    ? { ...candidate, expiryDate: event.target.value }
                                    : candidate,
                                ),
                              })
                            }
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={t('purchasing.receipts.removeLot', {
                              sku: line.sku,
                              index: index + 1,
                            })}
                            disabled={allocations.length === 1}
                            onClick={() =>
                              setEntry(line.id, {
                                allocations: allocations.filter(
                                  (candidate) => candidate.id !== allocation.id,
                                ),
                              })
                            }
                          >
                            <Trash2 className="size-4" aria-hidden />
                          </Button>
                        </div>
                      ))}

                      <div className="flex flex-wrap items-center gap-3">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() =>
                            setEntry(line.id, { allocations: [...allocations, newAllocation()] })
                          }
                        >
                          <Plus className="size-4" aria-hidden />
                          {t('purchasing.receipts.addAnotherLot')}
                        </Button>
                        <span
                          className={tooMany ? 'text-sm text-red-600' : 'text-muted text-sm'}
                          role={tooMany ? 'alert' : undefined}
                        >
                          {t('purchasing.receipts.lotTotal', {
                            counted,
                            remaining: line.quantityRemaining,
                          })}
                        </span>
                      </div>
                    </div>
                  )}
                </section>
              );
            })
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={t('purchasing.receipts.reviewSection')} />
        <CardBody className="space-y-2 text-sm">
          <p className="text-muted">{t('purchasing.receipts.reviewInherits')}</p>
          <div className="flex flex-wrap justify-between gap-3">
            <span className="text-muted">{t('purchasing.receipts.reviewLines')}</span>
            <span className="text-ink font-medium" dir="ltr">
              {lines.length}
            </span>
          </div>
          <div className="flex flex-wrap justify-between gap-3">
            <span className="text-muted">{t('purchasing.receipts.reviewQuantity')}</span>
            <span className="text-ink font-medium" dir="ltr">
              {totalCounted}
            </span>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
